import {
  IHC_ANTIBODIES_BASE_URL,
  IHC_ANTIBODIES_INDEX_URL,
} from 'constants/urls';
import {
  cleanText,
  getManufacturerDisplayName,
  isMeaningful,
  isSpecificSpecies,
  namesOf,
} from 'components/AntibodiesList/antibodyFacetConfig.mjs';

// Canonical URLs must point at the origin this build is actually served from. Prefer the
// live origin: a worktree's gitignored .env.local overrides .env.production, so reading
// REACT_APP_URL alone would leak a localhost canonical into a production build.
const resolveSiteUrl = () => {
  const origin = typeof window === 'undefined' ? null : window.location?.origin;

  if (origin && origin !== 'null') {
    return origin;
  }

  return (process.env.REACT_APP_URL || 'https://app.histowiz.com').replace(
    /\/+$/,
    '',
  );
};

const SITE_URL = resolveSiteUrl();

export const getAbsoluteUrl = (path) => `${SITE_URL}${path}`;

// JSON.stringify leaves '<' alone, so a field containing a closing script tag would end the
// script element early and inject markup into <head>. The catalogue fields are free text
// typed in LISA, so escape '<' as its unicode escape — still valid JSON, parsed identically.
export const toJsonLdString = (jsonLd) =>
  JSON.stringify(jsonLd).replace(/</g, '\\u003c');

export const ANTIBODIES_INDEX_PATH = IHC_ANTIBODIES_INDEX_URL;

export const ANTIBODIES_CATALOG_CANONICAL_URL = `${SITE_URL}${IHC_ANTIBODIES_BASE_URL}`;

export const ANTIBODIES_INDEX_CANONICAL_URL = `${SITE_URL}${ANTIBODIES_INDEX_PATH}`;

export const ANTIBODIES_CATALOG_TITLE =
  'IHC Antibody Catalog - Validated Antibodies | HistoWiz';

export const ANTIBODIES_CATALOG_DESCRIPTION =
  'Browse the HistoWiz IHC antibody catalog: antibodies validated on real tissue, with species reactivity, supported applications, positive control tissue and reference images.';

export const ANTIBODIES_INDEX_TITLE =
  'All IHC Antibodies - Full Catalog List | HistoWiz';

export const ANTIBODIES_INDEX_DESCRIPTION =
  'The complete list of HistoWiz-validated IHC antibodies. Every entry links to its catalog page with species reactivity, applications, positive control tissue and reference images.';

export const getAntibodyCanonicalUrl = (antibody) =>
  antibody?.uuid
    ? `${SITE_URL}${IHC_ANTIBODIES_BASE_URL}${antibody.uuid}`
    : ANTIBODIES_CATALOG_CANONICAL_URL;

export const getAntibodySeoTitle = (antibody) => {
  if (!antibody) return ANTIBODIES_CATALOG_TITLE;

  // A few records have no catalogue number; dropping it beats "Name -  - HistoWiz".
  return [antibody.name, antibody.catalog_number, 'HistoWiz']
    .map(cleanText)
    .filter(isMeaningful)
    .join(' - ');
};

const MAX_DESCRIPTION_LENGTH = 158;

const specificSpeciesNames = (entries) =>
  namesOf(entries).filter(isSpecificSpecies);

const listToSentence = (items) => {
  if (items.length <= 1) return items[0] || '';

  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
};

const truncateAtWord = (text, max) => {
  if (text.length <= max) return text;

  const clipped = text.slice(0, max + 1);
  const lastSpace = clipped.lastIndexOf(' ');

  return `${clipped.slice(0, lastSpace > 0 ? lastSpace : max)}…`;
};

export const getAntibodySeoDescription = (antibody) => {
  if (!antibody) return ANTIBODIES_CATALOG_DESCRIPTION;

  const sentences = [];
  const name = cleanText(antibody.name);
  const hostSpecies = cleanText(antibody.host_species?.name);
  const clonality = cleanText(antibody.clonality);
  const cloneNumber = cleanText(antibody.clone_number);
  const catalogNumber = cleanText(antibody.catalog_number);

  const identity = [
    isSpecificSpecies(hostSpecies) ? hostSpecies.toLowerCase() : null,
    isMeaningful(clonality) ? clonality.toLowerCase() : null,
  ]
    .filter(Boolean)
    .join(' ');

  const clone = isMeaningful(cloneNumber) ? ` (clone ${cloneNumber})` : '';
  const catalog = isMeaningful(catalogNumber)
    ? `, catalog #${catalogNumber}`
    : '';

  sentences.push(
    identity
      ? `${name} — ${identity} antibody${clone}${catalog}.`
      : `${name}${clone}${catalog}.`,
  );

  const applications = namesOf(antibody.applications);
  const validatedSpecies = specificSpeciesNames(
    antibody.histowiz_validated_species,
  ).map((species) => species.toLowerCase());

  if (applications.length && validatedSpecies.length) {
    sentences.push(
      `Validated for ${applications.join(', ')} in ${listToSentence(
        validatedSpecies,
      )} tissue.`,
    );
  } else if (applications.length) {
    sentences.push(`Validated for ${applications.join(', ')}.`);
  }

  const positiveControl = cleanText(antibody.positive_control_tissue).replace(
    /\.+$/,
    '',
  );

  if (isMeaningful(positiveControl)) {
    sentences.push(`Positive control tissue: ${positiveControl}.`);
  }

  return truncateAtWord(sentences.join(' '), MAX_DESCRIPTION_LENGTH);
};

const propertyValue = (name, value) => ({
  '@type': 'PropertyValue',
  name,
  value,
});

export const getAntibodyJsonLd = (antibody, description) => {
  if (!antibody?.uuid) return null;

  const hostSpecies = cleanText(antibody.host_species?.name);
  const additionalProperty = [
    propertyValue('Clone', cleanText(antibody.clone_number)),
    propertyValue('Clonality', cleanText(antibody.clonality)),
    propertyValue(
      'Host Species',
      isSpecificSpecies(hostSpecies) ? hostSpecies : '',
    ),
    propertyValue(
      'Validated Species',
      specificSpeciesNames(antibody.histowiz_validated_species).join(', '),
    ),
    propertyValue(
      'Reactive Species',
      specificSpeciesNames(antibody.reactive_species).join(', '),
    ),
    propertyValue('Applications', namesOf(antibody.applications).join(', ')),
    propertyValue(
      'Positive Control Tissue',
      cleanText(antibody.positive_control_tissue),
    ),
    propertyValue(
      'Cellular Localization',
      cleanText(antibody.cellular_localization),
    ),
    propertyValue('Research Area', cleanText(antibody.research_area)),
  ].filter(({ value }) => isMeaningful(value));

  const firstSlideImage = antibody.validated_slides?.find(
    (slide) => slide?.large_thumbnail_url,
  )?.large_thumbnail_url;
  const sku = cleanText(antibody.catalog_number);
  const brandName = getManufacturerDisplayName(antibody.manufacturer);

  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: cleanText(antibody.display_name || antibody.name),
    ...(isMeaningful(sku) ? { sku } : {}),
    description: description ?? getAntibodySeoDescription(antibody),
    url: getAntibodyCanonicalUrl(antibody),
    category: 'IHC Antibody',
    ...(firstSlideImage ? { image: [firstSlideImage] } : {}),
    ...(brandName ? { brand: { '@type': 'Brand', name: brandName } } : {}),
    ...(additionalProperty.length ? { additionalProperty } : {}),
  };
};

// Shared by the catalog index and the facet listings.
export const buildCollectionJsonLd = ({
  title,
  description,
  url,
  antibodies,
}) => ({
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  name: title,
  description,
  url,
  mainEntity: {
    '@type': 'ItemList',
    numberOfItems: antibodies.length,
    itemListElement: antibodies.map(
      ({ display_name: displayName, name, uuid }, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: cleanText(displayName || name),
        url: getAntibodyCanonicalUrl({ uuid }),
      }),
    ),
  },
});

export const buildBreadcrumbJsonLd = (trail) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: trail.map(({ name, url }, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name,
    item: url,
  })),
});
